import { Heading, Text } from '@cinch-labs/design-system'
import { Hero as THero, storyblokEditable } from '@cinch-nx/data-storyblok'
import { Env, readFromEnv } from '@cinch-nx/environments'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { Wrapper } from '@cinch-nx/shared-ui'
import cx from 'classnames'
import Image from 'next/legacy/image'
import { useEffect, useState } from 'react'
import styles from './generic-hero.module.css'

const APP_STORE_IMAGES = {
  ios: 'https://a.storyblok.com/f/132543/384x114/7266ad02b3/download-app-store.png',
  android:
    'https://a.storyblok.com/f/132543/564x168/9d227fc037/google-play-badge1.png',
} as const
const APP_STORE_BUTTON_DIMENSIONS = {
  height: 48,
  width: 164,
} as const

export const useAppStore = () => {
  const [isAppRoute, setIsAppRoute] = useState(false)

  const { trackEvent } = useAnalytics()
  const handleAppStoreClick = (label: string, value: string) => {
    trackEvent({
      eventName: 'App footer links',
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'app cta',
          label: label,
          action: 'view',
          value: value,
        },
      },
    })
  }

  useEffect(() => {
    setIsAppRoute(window.location.pathname.includes('/app'))
  }, [])

  return { isAppRoute, handleAppStoreClick }
}

interface AppStoreButtonsProps {
  onButtonClick: (label: string, value: string) => void
}

export const AppStoreButtons: React.FC<AppStoreButtonsProps> = ({
  onButtonClick,
}) => (
  <div className={styles.appHeroButtons}>
    <a
      href={readFromEnv(Env.IOSAppStoreUrl)}
      aria-label="app store badge"
      onClick={() => onButtonClick('apple', 'app store')}
    >
      <Image
        src={APP_STORE_IMAGES.ios}
        height={APP_STORE_BUTTON_DIMENSIONS.height}
        width={APP_STORE_BUTTON_DIMENSIONS.width}
        alt="Download on the App Store"
      />
    </a>
    <a
      href={readFromEnv(Env.AndroidAppStoreUrl)}
      onClick={() => onButtonClick('android', 'google play')}
      aria-label="google play badge"
    >
      <Image
        src={APP_STORE_IMAGES.android}
        height={APP_STORE_BUTTON_DIMENSIONS.height}
        width={APP_STORE_BUTTON_DIMENSIONS.width}
        alt="Get it on Google Play"
      />
    </a>
  </div>
)

export const getWrapperClassName = (
  heroImageIsPresent: boolean,
  isAppRoute: boolean,
) =>
  cx(heroImageIsPresent ? styles.root : styles.singleTitleWrapper, {
    [styles.singleTitleWrapper]: !heroImageIsPresent,
    [styles.appRoot]: isAppRoute,
  })

export const GenericHero: React.FC<THero> = (blok) => {
  const { title, body, image } = blok
  const heroImageIsPresent = !!image?.filename
  const { isAppRoute, handleAppStoreClick } = useAppStore()

  return (
    <Wrapper
      {...storyblokEditable(blok)}
      isSlim
      className={{
        inner: getWrapperClassName(heroImageIsPresent, isAppRoute),
      }}
    >
      {heroImageIsPresent && (
        <div
          className={cx(styles.imageWrapper, {
            [styles.appImageWrapper]: isAppRoute,
          })}
        >
          <Image
            src={image?.filename}
            alt={image?.alt ?? undefined}
            layout="fill"
            priority
            objectFit="contain"
          />
        </div>
      )}
      <div
        className={cx(styles.heroContent, {
          [styles.appHeroContent]: isAppRoute,
        })}
      >
        <Heading
          element="h1"
          className={cx(styles.title, {
            [styles.appTitle]: isAppRoute,
          })}
        >
          {title}
        </Heading>
        <Text
          className={cx(styles.body, {
            [styles.appBody]: isAppRoute,
          })}
        >
          {body}
        </Text>
        {isAppRoute && <AppStoreButtons onButtonClick={handleAppStoreClick} />}
      </div>
    </Wrapper>
  )
}
